/**
 * Momentum Predictor — Step 1 of SIMPLE.
 *
 * For each internal face, solve for intermediate velocity u*:
 *
 *   ρ_f · A · (u* - u^n) / dt = -(P_R - P_L) · A / dx
 *                                - f · ρ · u^n · |u^n| · A / (2D) · dx
 *                                - ρ · g · sin(θ) · A · dx
 *
 * This is an explicit predictor — no matrix solve needed.
 * The pressure gradient uses the current pressure field (P*).
 */

import type { PipeSegment } from '../types';
import { darcyFriction, reynolds, twoPhaseMultiplier } from '../core/frictionCorrelations';
import { saturationDensities } from '../core/fluidAdapter';
import {
  admitCanonicalPipeGeometry,
  admitFinitePositivePipeInput,
} from '../core/inputAdmission';

const G = 9.80665;

/**
 * Stability cap on the homogeneous two-phase friction multiplier φ².
 *
 * CLAUDE.md-documented trade-off: the raw homogeneous multiplier can reach
 * O(10²-10³) at low pressure (large ρ_f/ρ_g), which destabilizes the explicit
 * momentum predictor during transients. The cap is DELIBERATE and must stay
 * (N6) — but when it engages, wet-line pressure drop is under-predicted, so
 * the engagement is flagged on the pipe (twoPhaseFrictionCapEngaged) and
 * surfaced as a simState warning by the step adapter.
 */
export const TWO_PHASE_FRICTION_CAP = 50;

/**
 * Compute predicted mass flow rates at all internal faces of a pipe.
 *
 * @param pipe The pipe segment
 * @param dt Time step [s]
 * @param alpha_u Velocity under-relaxation factor (0-1)
 * @returns d_f coefficients needed for pressure correction
 */
export function momentumPredictor(
  pipe: PipeSegment,
  dt: number,
  alpha_u: number = 0.7,
): number[] {
  const { nCells, dx, diameter, roughness, inclination, area } = pipe;
  const d_f = new Array(nCells + 1).fill(0); // pressure-velocity coupling coefficients

  // Worker state and programmatic callers can bypass createPipe. Re-admit the
  // actual live geometry and EOS properties at the momentum boundary before
  // any division or friction evaluation. Validate individual cells rather
  // than their face average so one invalid value cannot be hidden by another.
  admitCanonicalPipeGeometry(pipe.id, diameter, roughness);
  for (let cellIndex = 0; cellIndex < nCells; cellIndex += 1) {
    const cell = pipe.cells[cellIndex];
    admitFinitePositivePipeInput(
      pipe.id,
      `pipes.${pipe.id}.cells[${cellIndex}].rho`,
      cell?.rho,
      'density in kg/m^3',
    );
    admitFinitePositivePipeInput(
      pipe.id,
      `pipes.${pipe.id}.cells[${cellIndex}].mu`,
      cell?.mu,
      'dynamic viscosity in Pa.s',
    );
  }

  // N6: reset the per-step cap diagnostic; set below if any face engages it.
  pipe.twoPhaseFrictionCapEngaged = false;

  for (let f = 0; f <= nCells; f++) {
    // Boundary faces: compute d_f coefficient but skip momentum prediction.
    // Boundary face mass flow is set by pressure correction (Dirichlet BCs)
    // or by extrapolation from interior (free BCs).
    if (f === 0 || f === nCells) {
      // SCAR-019: pressure-velocity coupling coefficient. mdot' = -d_f·(P'_R-P'_L).
      // From the explicit predictor ρ(u*-uⁿ)/dt = -ΔP/dx, a pressure correction
      // gives u' = dt·(-ΔP'/dx)/ρ ⇒ mdot' = ρA·u' = -(A·dt/dx)·ΔP' (ρ cancels).
      // The old A²/(ρ·dx) was dimensionally wrong (mdot' came out ~1000× too
      // small), so pressure correction computed enormous ΔP' → divergence.
      d_f[f] = Math.max(1e-10, area * dt / dx);
      continue;
    }

    const cellL = pipe.cells[f - 1];
    const cellR = pipe.cells[f];
    const rho_f = 0.5 * (cellL.rho + cellR.rho);
    const mu_f = 0.5 * (cellL.mu + cellR.mu);
    const u_old = pipe.faces_old[f].u;
    const mdot_old = pipe.faces_old[f].mdot;

    // Reynolds number at face
    const Re = reynolds(rho_f, u_old, diameter, mu_f);

    // All-regime Darcy factor. At exact rest Re=0 and f=0; the u|u| term is
    // also exactly zero, so startup is driven only by physical pressure and
    // gravity terms. No artificial turbulent-factor floor is applied.
    let friction = darcyFriction(Re, roughness, diameter);

    // Two-phase friction multiplier (homogeneous model) if either cell is two-phase
    if ((cellL.x >= 0 && cellL.x <= 1) || (cellR.x >= 0 && cellR.x <= 1)) {
      const x_f = 0.5 * (Math.max(0, Math.min(1, cellL.x)) + Math.max(0, Math.min(1, cellR.x)));
      if (x_f > 0 && x_f < 1) {
        const P_face = 0.5 * (cellL.P + cellR.P);
        const T_face = 0.5 * (cellL.T + cellR.T);
        let phi2 = 1;
        try {
          const [rhoSatL, rhoSatG] = saturationDensities(P_face);
          const muL = viscosityLiquid(T_face);
          const muG = viscosityVapour(T_face);
          phi2 = twoPhaseMultiplier(x_f, rhoSatL, rhoSatG, muL, muG);
        } catch {
          // Fallback: moderate estimate for when saturation lookup fails
          phi2 = 1 + x_f * 10;
        }
        // Cap multiplier to prevent numerical instability during transients
        // (documented CLAUDE.md trade-off — do NOT remove). N6: flag the pipe
        // when the cap engages so the dP under-prediction is user-visible.
        if (phi2 > TWO_PHASE_FRICTION_CAP) {
          pipe.twoPhaseFrictionCapEngaged = true;
        }
        friction *= Math.min(phi2, TWO_PHASE_FRICTION_CAP);
      }
    }

    // Pressure gradient: -(P_R - P_L) / dx
    const dPdx = -(cellR.P - cellL.P) / dx;

    // Friction force: -f · ρ · u · |u| / (2D)
    const frictionForce = -friction * rho_f * u_old * Math.abs(u_old) / (2 * diameter);

    // Gravity force: -ρ · g · sin(θ)
    const gravityForce = -rho_f * G * Math.sin(inclination);

    // Momentum equation: ρ_f · (u* - u^n) / dt = dPdx + friction + gravity
    const rhs = dPdx + frictionForce + gravityForce;
    const u_star = u_old + dt * rhs / rho_f;

    // Under-relax and clamp to physical range (guard against NaN and startup transient)
    const u_relaxed = isFinite(u_star) ? u_old + alpha_u * (u_star - u_old) : u_old;
    const u_new = Math.max(-50, Math.min(50, u_relaxed)); // Cap at 50 m/s (Mach < 0.15 for water)

    // Update face
    pipe.faces[f].mdot = rho_f * area * u_new;
    pipe.faces[f].u = u_new;

    // d_f coefficient: relates pressure correction to mass-flow correction.
    // mdot' = -d_f · (P'_R - P'_L), with d_f = A·dt/dx (SCAR-019). The previous
    // A²/(ρ·dx) form was dimensionally inconsistent (mdot' ≠ kg/s) and ~1000×
    // too small here, which drove the pressure correction to diverge.
    d_f[f] = Math.max(1e-10, area * dt / dx);
  }

  return d_f;
}

/** Estimate liquid water dynamic viscosity [Pa·s] from temperature [K]. */
function viscosityLiquid(T_K: number): number {
  const T = Math.max(273.15, Math.min(T_K, 623.15));
  return 2.414e-5 * Math.pow(10, 247.8 / (T - 140));
}

/** Estimate steam dynamic viscosity [Pa·s] from temperature [K]. */
function viscosityVapour(T_K: number): number {
  const T = Math.max(373.15, T_K);
  return 1.12e-5 * Math.pow(T / 373.15, 0.7);
}
